import { toChildArray } from 'preact';
import type { ComponentChildren, JSX, VNode } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import styles from './Tabs.module.css';

export interface TabProps {
  id: string;
  label: string;
  ariaLabel?: string;
  isActive?: boolean;
  children: JSX.Element | ((isActive: boolean) => JSX.Element);
}

export interface TabsClassNames {
  root?: string;
  tabBar?: string;
  tab?: string;
  activeTab?: string;
  content?: string;
  panel?: string;
}

interface TabsProps {
  initialTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  children: ComponentChildren;
  classNames?: TabsClassNames;
  showTabBar?: boolean;
}

function joinClassNames(...classNames: Array<string | undefined>): string {
  return classNames.filter(Boolean).join(' ');
}

function getAccessibleTabLabel(label: string): string {
  return label.replace(/\s+/g, ' ').trim();
}

export function Tab(_props: TabProps): null {
  return null;
}

/**
 * A simple Tabs component that renders its children as tab panels.
 * The active tab is controlled by the `initialTab` prop and internal state.
 * Tabs are given an isActive prop to allow for optimizations like lazy loading.
 **/
export function Tabs({
  initialTab = '',
  activeTab,
  onTabChange,
  children,
  classNames,
  showTabBar = true,
}: TabsProps): JSX.Element {
  const tabItems = useMemo(
    () => toChildArray(children) as VNode<TabProps>[],
    [children],
  );

  const firstTabId = tabItems[0]?.props.id ?? '';
  const resolvedInitialTab = initialTab || firstTabId;
  const [uncontrolledActiveTab, setUncontrolledActiveTab] =
    useState(resolvedInitialTab);
  const currentActiveTab = activeTab ?? uncontrolledActiveTab;

  useEffect(() => {
    setUncontrolledActiveTab(resolvedInitialTab);
  }, [resolvedInitialTab]);

  const selectTab = (id: string) => {
    if (activeTab === undefined) {
      setUncontrolledActiveTab(id);
    }

    onTabChange?.(id);
  };

  return (
    <div className={joinClassNames(styles.root, classNames?.root)}>
      {showTabBar && (
        <div className={joinClassNames(styles.tabBar, classNames?.tabBar)}>
          {tabItems.map(({ props: { id, label, ariaLabel } }) => {
            const isActive = currentActiveTab === id;
            return (
              <button
                key={id}
                aria-label={ariaLabel ?? getAccessibleTabLabel(label)}
                className={joinClassNames(
                  styles.tab,
                  classNames?.tab,
                  isActive ? styles.activeTab : undefined,
                  isActive ? classNames?.activeTab : undefined,
                )}
                onClick={() => selectTab(id)}
                type="button"
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div className={joinClassNames(styles.content, classNames?.content)}>
        {tabItems.map(({ props: { id, children: tabChildren } }) => {
          const isActive = currentActiveTab === id;
          return (
            <div
              key={id}
              data-tab-panel={id}
              className={joinClassNames(
                styles.panel,
                classNames?.panel,
                isActive ? undefined : styles.panelInactive,
              )}
              aria-hidden={!isActive}
            >
              {typeof tabChildren === 'function'
                ? tabChildren(isActive)
                : tabChildren}
            </div>
          );
        })}
      </div>
    </div>
  );
}
